import { client } from "actions";
import { updateBulkImgListByID } from "actions/bulk-upload-action";
import { persistBulkStatusProjection } from "./bulkUploadPersistence";
import { getBulkRowGuid, getBulkRowTuples } from "./bulkUploadProjection";

const nonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const normalized = (value) =>
  value === undefined || value === null
    ? ""
    : String(value).trim().toLowerCase();

const currentScope = (isCurrent) => {
  try {
    return typeof isCurrent !== "function" || isCurrent();
  } catch (error) {
    return false;
  }
};

const statusModel = (status) => status?.model || status?.Model;

const requestIdentity = (guid, model) => {
  const normalizedGuid = normalized(guid);
  const normalizedModel = normalized(model);
  return normalizedGuid && normalizedModel
    ? `${normalizedGuid}|${normalizedModel}`
    : "";
};

const snapshotRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const structuredRows = rows.filter(
    (row) => Array.isArray(row?.status) && row.status.length > 0
  );
  if (structuredRows.length === 0) return null;
  const identities = new Set();
  const requestRows = [];
  for (const row of structuredRows) {
    const tuples = getBulkRowTuples(row);
    if (tuples.length !== row.status.length) return null;
    const guid = String(row.guid || row.info?.guid || "").trim();
    if (!guid || getBulkRowGuid(row) !== normalized(guid)) return null;
    const algorithms = [];
    for (const status of row.status) {
      const model = statusModel(status);
      const identity = requestIdentity(guid, model);
      if (!identity || identities.has(identity)) return null;
      identities.add(identity);
      algorithms.push(String(model).trim());
    }
    requestRows.push({ guid, algorithms });
  }
  return requestRows;
};

export const requestBulkUploadSnapshot = ({
  authToken,
  orgId,
  rows,
  signal,
}) =>
  client.post(`/report/orgz/${encodeURIComponent(String(orgId))}`, rows, {
    headers: {
      Accept: "*/*",
      authorization: authToken,
    },
    ...(signal ? { signal } : {}),
  });

export const reconcileBulkUploadSnapshot = async ({
  authToken,
  orgId,
  rows,
  signal,
  isCurrent = () => true,
  request = requestBulkUploadSnapshot,
  persist = persistBulkStatusProjection,
  publish,
} = {}) => {
  if (
    !nonemptyString(authToken) ||
    orgId === undefined ||
    orgId === null ||
    typeof request !== "function" ||
    typeof persist !== "function" ||
    typeof publish !== "function" ||
    !currentScope(isCurrent)
  ) {
    return { outcome: "ignored" };
  }
  const requestedRows = snapshotRows(rows);
  if (!requestedRows) return { outcome: "fallback" };

  let response;
  try {
    response = await request({
      authToken,
      orgId,
      rows: requestedRows,
      signal,
    });
  } catch (error) {
    return currentScope(isCurrent)
      ? { outcome: "fallback" }
      : { outcome: "ignored" };
  }
  if (!currentScope(isCurrent)) return { outcome: "ignored" };
  if (!Array.isArray(response?.data)) return { outcome: "fallback" };

  const expectedIdentities = new Set(
    requestedRows.flatMap(({ guid, algorithms }) =>
      algorithms.map((model) => requestIdentity(guid, model))
    )
  );
  if (response.data.length !== expectedIdentities.size) {
    return { outcome: "fallback" };
  }

  const statusesByIdentity = new Map();
  for (const status of response.data) {
    const identity = requestIdentity(status?.guid, statusModel(status));
    if (
      !identity ||
      !expectedIdentities.has(identity) ||
      !nonemptyString(status?.status) ||
      statusesByIdentity.has(identity)
    ) {
      return { outcome: "fallback" };
    }
    statusesByIdentity.set(identity, status.status);
  }

  let changed = false;
  const nextRows = rows.map((row) => {
    if (!Array.isArray(row?.status) || row.status.length === 0) return row;
    const guid = row.guid || row.info?.guid;
    const nextStatus = row.status.map((status) => {
      const identity = requestIdentity(guid, statusModel(status));
      if (!statusesByIdentity.has(identity)) return null;
      const nextValue = statusesByIdentity.get(identity);
      if (nextValue !== status.status) changed = true;
      return { ...status, status: nextValue };
    });
    return nextStatus.some((status) => status === null)
      ? null
      : { ...row, status: nextStatus };
  });
  if (nextRows.some((row) => row === null)) return { outcome: "fallback" };
  if (!changed) return { outcome: "ignored", rows };

  try {
    for (let index = 0; index < nextRows.length; index += 1) {
      const previous = rows[index];
      const row = nextRows[index];
      if (
        Array.isArray(previous?.status) &&
        previous.status.some(
          (status, statusIndex) =>
            status.status !== row.status[statusIndex]?.status
        )
      ) {
        await persist({ row, publish, isCurrent });
        if (!currentScope(isCurrent)) return { outcome: "ignored" };
      }
    }
  } catch (error) {
    return currentScope(isCurrent)
      ? { outcome: "fallback" }
      : { outcome: "ignored" };
  }
  return { outcome: "applied", rows: nextRows };
};

export const resyncBulkUploadStatuses = (options) => (dispatch) =>
  reconcileBulkUploadSnapshot({
    ...options,
    publish: (row) => dispatch(updateBulkImgListByID(row)),
  });

export default reconcileBulkUploadSnapshot;
